import { extractCaseIds, parseCaseTokens } from "@/api/club/caseTokens";
import type { ClubFeedItem, EventHelperRole } from "@/api/club/ClubApi";
import {
  useCaseChips,
  useCastPollVote,
  useCloseClubPoll,
  useClubComments,
  useSetClubPostOfficial,
  useSetClubPostPinned,
  useSetClubReaction,
  useSetClubWatch,
  useSetClubWatchNotify,
  useWithdrawClubItem,
} from "@/api/club/useClub";
import { useAuth } from "@/auth/AuthContext";
import { formatTimestampDE } from "@/utils/isoDate";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { CaseChipView } from "@/ui/components/club/CaseChip";
import { ClubCommentList } from "@/ui/components/club/ClubCommentList";
import { EventHelpersPanel } from "@/ui/components/club/EventHelpersPanel";
import { EventRsvpPanel } from "@/ui/components/club/EventRsvpPanel";
import { ReactionBar } from "@/ui/components/club/ReactionBar";
import { WatchToggle } from "@/ui/components/club/WatchToggle";
import { ErrorState, LoadingState } from "@/ui/components/StateViews";
import React, { useEffect, useMemo, useState } from "react";
import { Text, View } from "react-native";

function BodyWithChips({ text }: { text: string | null | undefined }) {
  const s = useStyles();
  const caseIds = useMemo(() => extractCaseIds(text), [text]);
  const { byId } = useCaseChips(caseIds, caseIds.length > 0);
  if (!text) return null;
  const segments = parseCaseTokens(text);
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
      {segments.map((seg, i) =>
        seg.kind === "text" ? (
          <Text key={i} style={s.textBody}>
            {seg.value}
          </Text>
        ) : (
          <CaseChipView key={i} chip={byId[seg.caseId]} />
        )
      )}
    </View>
  );
}

export function ClubItemDetail({
  item,
  organizationId,
  canManageOfficial,
  canManageEvent,
  disabled,
}: {
  item: ClubFeedItem | null;
  organizationId?: string;
  canManageOfficial?: boolean;
  canManageEvent?: boolean;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const { user } = useAuth();
  const { comments, loading: commentsLoading } = useClubComments(
    item?.item_id,
    !!item
  );

  const react = useSetClubReaction(organizationId);
  const watch = useSetClubWatch(organizationId);
  const watchNotify = useSetClubWatchNotify(organizationId);
  const castVote = useCastPollVote(organizationId);
  const closePoll = useCloseClubPoll(organizationId);
  const withdraw = useWithdrawClubItem(organizationId);
  const setOfficial = useSetClubPostOfficial(organizationId);
  const setPinned = useSetClubPostPinned(organizationId);

  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const options = item?.poll_options ?? [];

  useEffect(() => {
    if (!item || item.item_type !== "poll") return;
    const mine = (item.poll_options ?? [])
      .filter((o) => o.my_vote)
      .map((o) => o.id);
    setSelectedOptions(mine);
  }, [item?.item_id, item?.item_type]);

  if (!item) {
    return <LoadingState message="Eintrag laden…" />;
  }

  if (item.status === "removed") {
    return (
      <ErrorState message="Dieser Eintrag wurde entfernt." />
    );
  }

  const isMine = !!user?.id && item.created_by === user.id;
  const withdrawn = item.status === "withdrawn";
  const helpers = (item.helper_roles as EventHelperRole[] | undefined) ?? [];

  const toggleOption = (id: string) => {
    if (item.allow_multiple) {
      setSelectedOptions((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    } else {
      setSelectedOptions([id]);
    }
  };

  return (
    <View style={{ gap: theme.space.lg }}>
      <View
        style={{
          backgroundColor: theme.color.surface,
          borderRadius: theme.radius.md,
          borderWidth: 1,
          borderColor: theme.color.border,
          padding: theme.space.md,
          gap: theme.space.xs,
        }}
      >
        <Text
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.h2,
            fontWeight: "800",
          }}
        >
          {withdrawn
            ? "Zurückgezogen"
            : item.title?.trim() ||
              (item.item_type === "post" ? "Beitrag" : "Eintrag")}
        </Text>
        <Text
          style={{
            color: theme.color.muted,
            fontSize: theme.fontSize.tiny,
            fontWeight: "500",
          }}
        >
          {[
            item.author_display_name ?? "Mitglied",
            item.item_type === "post"
              ? "Beitrag"
              : item.item_type === "poll"
                ? "Umfrage"
                : "Veranstaltung",
            item.is_official ? "Offiziell" : null,
            item.pinned_at ? "Angepinnt" : null,
            formatTimestampDE(item.created_at),
          ]
            .filter(Boolean)
            .join(" · ")}
        </Text>
      </View>

      {withdrawn ? (
        <Text style={[s.textBody, { fontStyle: "italic", color: theme.color.muted }]}>
          Inhalt zurückgezogen
        </Text>
      ) : (
        <>
          {item.item_type === "post" ? (
            <BodyWithChips text={item.body} />
          ) : null}

          {item.item_type === "poll" ? (
            <View style={{ gap: theme.space.md }}>
              <BodyWithChips text={item.body} />
              {item.is_closed || item.closed_at ? (
                <Text style={[s.textTiny, { color: theme.color.warning }]}>
                  Umfrage geschlossen
                </Text>
              ) : null}
              {options.map((opt) => {
                const selected =
                  selectedOptions.includes(opt.id) || !!opt.my_vote;
                return (
                  <AppButton
                    key={opt.id}
                    variant={selected ? "primary" : "outline"}
                    selected={selected}
                    disabled={
                      disabled ||
                      !!item.is_closed ||
                      !!item.closed_at ||
                      castVote.isPending
                    }
                    onPress={() => toggleOption(opt.id)}
                  >
                    {`${opt.label}${
                      opt.vote_count != null
                        ? ` · ${opt.vote_count} Stimmen`
                        : ""
                    }`}
                  </AppButton>
                );
              })}
              {!item.is_closed && !item.closed_at ? (
                <AppButton
                  loading={castVote.isPending}
                  disabled={disabled}
                  onPress={() =>
                    castVote.mutate({
                      pollItemId: item.item_id,
                      optionIds: selectedOptions,
                    })
                  }
                >
                  Stimme abgeben
                </AppButton>
              ) : null}
              {isMine && !item.is_closed && !item.closed_at ? (
                <AppButton
                  variant="secondary"
                  loading={closePoll.isPending}
                  onPress={() => closePoll.mutate(item.item_id)}
                >
                  Umfrage schließen
                </AppButton>
              ) : null}
            </View>
          ) : null}

          {item.item_type === "event" ? (
            <View style={{ gap: theme.space.md }}>
              <Text style={s.textBody}>
                {formatTimestampDE(item.starts_at as string)}
                {item.ends_at || item.ends_at_event
                  ? ` – ${formatTimestampDE(
                      (item.ends_at || item.ends_at_event) as string
                    )}`
                  : ""}
                {item.all_day ? " (ganztägig)" : ""}
              </Text>
              {item.location_text ? (
                <Text style={s.textBody}>Ort: {item.location_text}</Text>
              ) : null}
              {item.online_url ? (
                <Text style={s.textBody}>Online: {item.online_url}</Text>
              ) : null}
              <BodyWithChips text={item.description ?? item.body} />
              <EventRsvpPanel
                item={item}
                organizationId={organizationId}
                disabled={disabled}
              />
              <EventHelpersPanel
                itemId={item.item_id}
                organizationId={organizationId}
                roles={helpers}
                canManage={canManageEvent || isMine}
                disabled={disabled || !!item.cancelled_at}
              />
            </View>
          ) : null}
        </>
      )}

      {!withdrawn ? (
        <View style={{ gap: theme.space.md }}>
          <ReactionBar
            myReaction={item.my_reaction}
            counts={item.reaction_counts}
            disabled={disabled || react.isPending}
            onSelect={(key) =>
              react.mutate({
                itemId: item.item_id,
                reactionKey: key,
              })
            }
          />
          <WatchToggle
            watching={!!item.watching}
            notifyEnabled={item.watch_notify !== false}
            disabled={disabled}
            onToggleWatch={(watching) =>
              watch.mutate({ itemId: item.item_id, watching })
            }
            onToggleNotify={(enabled) =>
              watchNotify.mutate({ itemId: item.item_id, enabled })
            }
          />
        </View>
      ) : null}

      {canManageOfficial && item.item_type === "post" && !withdrawn ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.sm }}>
          <AppButton
            variant="secondary"
            onPress={() =>
              setOfficial.mutate({
                itemId: item.item_id,
                isOfficial: !item.is_official,
              })
            }
          >
            {item.is_official ? "Offiziell entfernen" : "Als offiziell"}
          </AppButton>
          {item.is_official ? (
            <AppButton
              variant="secondary"
              onPress={() =>
                setPinned.mutate({
                  itemId: item.item_id,
                  pinned: !item.pinned_at,
                })
              }
            >
              {item.pinned_at ? "Loslösen" : "Anpinnen"}
            </AppButton>
          ) : null}
        </View>
      ) : null}

      {isMine && !withdrawn && item.item_type !== "event" ? (
        <AppButton
          variant="ghost"
          loading={withdraw.isPending}
          onPress={() => withdraw.mutate(item.item_id)}
        >
          Zurückziehen
        </AppButton>
      ) : null}

      <ClubCommentList
        itemId={item.item_id}
        organizationId={organizationId}
        comments={comments}
        loading={commentsLoading}
        disabled={disabled}
      />
    </View>
  );
}
